import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { getApiBaseUrl } from "../../config/appSettings";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Read a row property supporting camelCase and PascalCase without treating `false` as missing. */
function getRowValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  if (Object.prototype.hasOwnProperty.call(row, pascalKey)) return row[pascalKey];
  if (row[key] !== undefined) return row[key];
  if (row[pascalKey] !== undefined) return row[pascalKey];
  return undefined;
}

function getRowBool(row, key, fallback = true) {
  const value = getRowValue(row, key);
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const text = String(value).trim().toLowerCase();
  if (text === "true" || text === "1" || text === "yes") return true;
  if (text === "false" || text === "0" || text === "no") return false;
  return fallback;
}

export function EntityCrudPage({
  title,
  subtitle,
  endpoint,
  customerScoped = false,
  listColumns = [],
  fields = [],
  itemLabel
}) {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(0);
  const [form, setForm] = useState(() => buildInitialForm(fields));
  const [isFormOpen, setIsFormOpen] = useState(false);

  const singularLabel = itemLabel || (title.endsWith("s") ? title.slice(0, -1) : title);

  const canPickCustomer = customerScoped && isAdmin;
  const effectiveCustomerId = useMemo(() => {
    if (!customerScoped) return null;
    if (isAdmin) return selectedCustomerId ? Number(selectedCustomerId) : null;
    return Number(user?.customerId || 0) || null;
  }, [customerScoped, isAdmin, selectedCustomerId, user]);

  useEffect(() => {
    if (!canPickCustomer) return;

    let cancelled = false;
    const loadCustomers = async () => {
      const res = await apiGet("/api/Customers");
      if (cancelled || !res.ok) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setCustomers(list);
      // Use functional update so a selection made while this request was in-flight
      // is not overwritten by the default first customer.
      setSelectedCustomerId((prev) => prev || (list[0]?.id ? String(list[0].id) : ""));
    };
    void loadCustomers();
    return () => {
      cancelled = true;
    };
  }, [canPickCustomer]);

  const load = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    if (search.trim()) query.set("search", search.trim());
    if (customerScoped && effectiveCustomerId) query.set("customerId", String(effectiveCustomerId));
    const path = query.toString() ? `${endpoint}?${query}` : endpoint;
    const res = await apiGet(path);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || `Failed to load ${title}`), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [endpoint, customerScoped, effectiveCustomerId, search, title, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    const initial = buildInitialForm(fields);
    if (customerScoped && effectiveCustomerId) {
      initial.customerId = effectiveCustomerId;
    }
    setForm(initial);
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const next = buildInitialForm(fields);
    for (const field of fields) {
      if (field.type === "checkbox") {
        next[field.key] = getRowBool(row, field.key, field.defaultValue ?? true);
      } else {
        next[field.key] = normalizeFieldValue(field, getRowValue(row, field.key));
      }
    }
    if (customerScoped) {
      next.customerId = Number(getRowValue(row, "customerId") ?? effectiveCustomerId ?? 0);
    }
    setForm(next);
    setIsFormOpen(true);
  };

  const resetForm = () => {
    setEditing(null);
    setForm(buildInitialForm(fields));
    setIsFormOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (customerScoped && !effectiveCustomerId && !form.customerId) {
      toast.push("Please select a customer", "error");
      return;
    }

    const payload = {
      ...(editing ? { id: editing.id } : {})
    };
    for (const field of fields) {
      payload[field.key] = normalizeSaveValue(field, form[field.key]);
    }
    // Keep legacy Inactive entities working when the form has no checkbox for it.
    if (!Object.prototype.hasOwnProperty.call(payload, "inactive") && !Object.prototype.hasOwnProperty.call(payload, "isActive")) {
      payload.inactive = false;
    }
    if (Object.prototype.hasOwnProperty.call(payload, "isActive")) {
      payload.isActive = Boolean(payload.isActive);
    }
    if (customerScoped) {
      payload.customerId = isAdmin ? Number(form.customerId || effectiveCustomerId || 0) : Number(effectiveCustomerId);
    }

    if (!validateRequired(fields, payload, toast)) return;

    setSaving(true);
    const res = editing
      ? await apiPut(`${endpoint}/${editing.id}`, payload)
      : await apiPost(endpoint, payload);
    setSaving(false);

    if (!res.ok) {
      toast.push(String(res.error || `Failed to save ${title}`), "error");
      return;
    }

    toast.push(editing ? "Updated successfully" : "Created successfully", "success");
    resetForm();
    await load();
  };

  const removeRow = async (row) => {
    const confirmed = window.confirmDialog
      ? await window.confirmDialog(`Delete this ${singularLabel.toLowerCase()}?`)
      : window.confirm(`Delete this ${singularLabel.toLowerCase()}?`);
    if (!confirmed) return;
    setDeletingId(row.id);
    const res = await apiDelete(`${endpoint}/${row.id}`);
    setDeletingId(0);
    if (!res.ok) {
      toast.push(String(res.error || `Failed to delete ${title}`), "error");
      return;
    }
    toast.push("Deleted successfully", "success");
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Add New
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
          <input
            className={inputClass}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {canPickCustomer && (
            <select
              className={inputClass}
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              {customers.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="bg-slate-50">
                  {listColumns.map((col) => (
                    <th key={col.key} className="px-4 py-3 text-xs font-semibold text-slate-500">{col.label}</th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    {listColumns.map((col) => (
                      <td key={col.key} className="px-4 py-3 text-slate-700">
                        {renderValue(col, row)}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => openEdit(row)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => void removeRow(row)}
                          disabled={deletingId === row.id}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                        >
                          {deletingId === row.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={listColumns.length + 1} className="px-4 py-12 text-center text-sm text-slate-500">No records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" role="presentation">
          <button type="button" className="absolute inset-0 bg-slate-900/50" onClick={resetForm} aria-label="Close" />
          <div className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900">{editing ? `Edit ${singularLabel}` : `Add ${singularLabel}`}</h2>
            <form onSubmit={save} className="mt-4 space-y-3">
              {canPickCustomer && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Customer *</label>
                  <select
                    className={inputClass}
                    value={form.customerId ? String(form.customerId) : ""}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setForm((prev) => ({ ...prev, customerId: toNumber(nextId, 0) }));
                      // Keep list filter aligned with the customer being edited/created
                      // so the new row remains visible after save.
                      if (nextId) setSelectedCustomerId(nextId);
                    }}
                  >
                    <option value="">Select customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name || c.code || `Customer ${c.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {fields.map((field) => (
                <div key={field.key}>
                  {field.type !== "image" && field.type !== "checkbox" && (
                    <label className="mb-1.5 block text-xs font-medium text-slate-600">
                      {field.label}
                      {field.required ? " *" : ""}
                    </label>
                  )}
                  {field.type === "image" ? (
                    <ImageUploadField
                      label={`${field.label}${field.required ? " *" : ""}`}
                      value={String(form[field.key] ?? "")}
                      onChange={(nextValue) => setForm((prev) => ({ ...prev, [field.key]: nextValue }))}
                      labelClassName="mb-1.5 block text-xs font-medium text-slate-600"
                    />
                  ) : field.type === "checkbox" ? (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(form[field.key])}
                        onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600/30"
                      />
                      <span>
                        {field.label}
                        {field.required ? " *" : ""}
                      </span>
                    </label>
                  ) : field.type === "textarea" ? (
                    <textarea
                      className={`${inputClass} min-h-24`}
                      value={String(form[field.key] ?? "")}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  ) : (
                    <input
                      className={inputClass}
                      type={field.type === "number" ? "number" : "text"}
                      value={String(form[field.key] ?? "")}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: field.type === "number" ? toNumber(e.target.value, 0) : e.target.value }))}
                    />
                  )}
                </div>
              ))}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={resetForm} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function buildInitialForm(fields) {
  const initial = {};
  for (const field of fields) {
    if (field.type === "number") initial[field.key] = field.defaultValue ?? 0;
    else if (field.type === "checkbox") initial[field.key] = field.defaultValue ?? true;
    else initial[field.key] = field.defaultValue ?? "";
  }
  return initial;
}

function normalizeFieldValue(field, value) {
  if (field.type === "number") return toNumber(value, 0);
  if (field.type === "checkbox") return Boolean(value);
  return value ?? "";
}

function normalizeSaveValue(field, value) {
  if (field.type === "number") return toNumber(value, 0);
  if (field.type === "checkbox") return Boolean(value);
  const text = String(value ?? "").trim();
  return text.length ? text : null;
}

function validateRequired(fields, payload, toast) {
  for (const field of fields) {
    if (!field.required) continue;
    if (field.type === "checkbox") continue;
    const value = payload[field.key];
    if (field.type === "number") {
      if (!Number.isFinite(Number(value))) {
        toast.push(`${field.label} is required`, "error");
        return false;
      }
      continue;
    }
    if (!String(value ?? "").trim()) {
      toast.push(`${field.label} is required`, "error");
      return false;
    }
  }
  return true;
}

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
}

function renderValue(col, row) {
  const value = getRowValue(row, col.key);
  if (col.render) return col.render(value, row);
  if (col.type === "image") {
    const src = resolveMediaUrl(value);
    if (!src) return "-";
    return (
      <img
        src={src}
        alt=""
        className="h-12 w-12 rounded-lg border border-slate-200 bg-slate-50 object-cover"
      />
    );
  }
  if (col.type === "boolean") {
    return getRowBool(row, col.key, false) ? "Yes" : "No";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (value === null || value === undefined || value === "") return "-";
  const text = String(value);
  if (col.oneLine) {
    return <span className="block max-w-[380px] truncate">{text}</span>;
  }
  return text;
}
